import { inject, injectable } from 'inversify'
import { BrowserMicroSentryClient } from '@micro-sentry/browser'
import type { User as MicrosentryUser } from '@micro-sentry/core'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { App } from '@/composition/vue/compositionApi'
import type { Config } from '@/core/types'
import type { MonitoringServiceInterface } from '@/services/monitoring/types'
import type User from '@/data/models/domain/User'
import { SERVICE_TYPES } from '@/core/container/types'
import { LogLevel } from '@/core/logger/types'
import { getUnhandledRejectionEventHandler } from '@/utils/unhandledRejection'
import { TmBaseError } from '@/core/error/tmBaseError'
import { SENTRY_IGNORE_ERRORS } from '@/services/monitoring/ignoreErrors'

@injectable()
export class MicroSentryMonitoringService implements MonitoringServiceInterface {
  private microSentryClient: BrowserMicroSentryClient | null

  private errorEventListener: (evt: ErrorEvent) => unknown

  private unhandledRejectionEventListener: (evt: PromiseRejectionEvent) => unknown

  constructor(@inject(SERVICE_TYPES.Config) protected readonly config: Config) {}

  public init(_app: App): void {
    this.microSentryClient = new BrowserMicroSentryClient({
      dsn: this.config.sentry.dsn,
      environment: APP_BUILD_ENV,
      release: [APP_NAME, APP_VERSION].join('@'),
      ignoreErrors: SENTRY_IGNORE_ERRORS,
    })
    this.errorEventListener = this.errorEventHandler.bind(this)
    this.unhandledRejectionEventListener = getUnhandledRejectionEventHandler(this.logError.bind(this))
    window.addEventListener('error', this.errorEventListener)
    window.addEventListener('unhandledrejection', this.unhandledRejectionEventListener)
  }

  public setUser(user: User | null) {
    // oxlint-disable-next-line no-unneeded-ternary -- ternary needed: type cast applies only to non-null branch
    this.microSentryClient?.setUser(user ? (user as unknown as MicrosentryUser) : {})
  }

  public logInfo(message: string, logLevel: LogLevel = LogLevel.INFO) {
    if (logLevel === LogLevel.NULL) return
    this.microSentryClient?.captureMessage(message)
  }

  public logError(error: Error, logLevel: LogLevel = LogLevel.ERROR) {
    if (!error || logLevel === LogLevel.NULL) {
      return
    }
    if (error instanceof TmBaseError && !error.shouldBeMonitored()) {
      return
    }
    if (!error.message) {
      error.message = `An unknown error occurred, error: ${JSON.stringify(error)}`
    }
    this.microSentryClient?.report(error)
  }

  public destroy() {
    window.removeEventListener('error', this.errorEventListener)
    window.removeEventListener('unhandledrejection', this.unhandledRejectionEventListener)
    this.microSentryClient?.destroy()
  }

  private errorEventHandler(evt: ErrorEvent) {
    if (evt.error instanceof TmBaseError && !evt.error.shouldBeMonitored()) {
      return false
    }
    let err: any = evt // special cast to any. MicroSentryClient can report objects too
    if (!evt?.message) {
      err = { ...evt, message: "The error was caused due to an 'error' event", stack: evt.error }
    }
    this.logError(err)
    return false
  }
}
